/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Observable } from 'rxjs';
import { JsonObject } from 'type-fest';
import { HttpMethod } from '../../Constants/Methods/Methods.constant';
import { HttpRequestOptions } from '../../Types/RequestOptions/RequestOptions.type';
import { HttpAdapter } from './Adapters/Http.adapter';
import { HttpAdapterContract } from './Adapters/Http.adapter.dto';
import { HttpServiceNewDto } from './Http.service.dto';

/**
 * Allows http requests to be made.
 */
export class HttpService implements HttpAdapterContract {
  /**
   * The http adapter to use.
   */
  private readonly _httpAdapter: HttpAdapter;

  /**
   * Creates an instance of HttpService.
   * @param dto The options to accept.
   */
  public constructor({ httpAdapter }: HttpServiceNewDto) {
    this._httpAdapter = httpAdapter;
  }

  /**
   * Make a DELETE request.
   * @template T The http request body type.
   * @template R The http response type.
   * @returns The http response.
   */
  public delete<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this._httpAdapter.delete<T, R>(url, options);
  }

  /**
   * Make a GET request.
   * @template T The http request body type.
   * @template R The http response type.
   * @returns The http response.
   */
  public get<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this._httpAdapter.get<T, R>(url, options);
  }

  /**
   * Make a HEAD request.
   * @template T The http request body type.
   * @template R The http response type.
   * @returns The http response.
   */
  public head<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this._httpAdapter.head<T, R>(url, options);
  }

  /**
   * Make an OPTIONS request.
   * @template T The http request body type.
   * @template R The http response type.
   * @returns The http response.
   */
  public options<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this._httpAdapter.options<T, R>(url, options);
  }

  /**
   * Make a PATCH request.
   * @template T The http request body type.
   * @template R The http response type.
   * @returns The http response.
   */
  public patch<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this._httpAdapter.patch<T, R>(url, options);
  }

  /**
   * Make a POST request.
   * @template T The http request body type.
   * @template R The http response type.
   * @returns The http response.
   */
  public post<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this._httpAdapter.post<T, R>(url, options);
  }

  /**
   * Make a PUT request.
   * @template T The http request body type.
   * @template R The http response type.
   * @returns The http response.
   */
  public put<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this._httpAdapter.put<T, R>(url, options);
  }

  /**
   * Make a request.
   * @template T The http request body type.
   * @template R The http response type.
   * @returns The http response.
   */
  public request<T = JsonObject, R = unknown>(
    method: HttpMethod,
    url: string,
    options: HttpRequestOptions<T> = {},
  ): Observable<R> {
    return this._httpAdapter.request<T, R>(method, url, options);
  }
}
